import { Injectable } from '@angular/core';

import { SportsCacheService } from '@frontend/sports/betting-offer/feature/sports-cache';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { CountItem } from '@frontend/sports/common/core/data-access/sport-model';
import { TreeSeparatorItem } from '@frontend/sports/item/feature';
import { Tournament } from '@frontend/sports/types/components/tournaments';
import { TopItemsPayload, TopItemsType } from '@frontend/sports/types/components/widget/types';
import { cloneDeep, toInteger } from 'lodash-es';
import { Observable, forkJoin, map } from 'rxjs';

import { LazyTreeItem, SportTreeItem } from './competition-list.models';
import { CompetitionTreeService } from './competition-tree.service';
import { CompetitionListService } from './services/competition-list.service';
import { SportTreeService } from './sport-tree.service';

interface TreeDataSource {
    source?: CountItem | CountItem[];
    sports?: CountItem[];
    tournaments?: Tournament[];
}

@Injectable({ providedIn: 'root' })
export class TopItemsWidgetService {
    constructor(
        private competitionList: CompetitionListService,
        private cache: SportsCacheService,
        private sitecore: Sitecore,
        private competitionMapper: CompetitionTreeService,
        private sportMapper: SportTreeService,
    ) {}

    buildItemTree(
        data: TopItemsPayload,
        sportId?: number,
    ): Observable<{
        tree: SportTreeItem | undefined;
        type: TopItemsType;
    }> {
        let data$: Observable<TreeDataSource | undefined>;

        if (data.type === TopItemsType.TopSports) {
            data$ = this.getSportList();
        } else {
            data$ = this.getSport(sportId!);
        }

        return data$.pipe(
            map((itemData) => {
                return this.getSource(itemData?.source, itemData?.sports, itemData?.tournaments, data.type);
            }),
            map((tree) => ({ tree, type: data.type })),
        );
    }

    private getSport(sportId: number): Observable<TreeDataSource | undefined> {
        return this.competitionList.getSportTree(sportId).pipe(
            map((result) => {
                if (!result?.sport) {
                    return {};
                }

                return { source: result.sport, tournaments: result.tournaments ?? [] };
            }),
        );
    }

    private getSportList(): Observable<TreeDataSource | undefined> {
        const lobbyConfig = this.sitecore.lobby;

        const topCount = toInteger(lobbyConfig.SortedSportsLeftColumnDisplayLimit);
        const topReorder = { id: SportConstant.Horses, location: toInteger(lobbyConfig.HorseRacingLeftColumnPosition) };

        return forkJoin([this.competitionList.getSportList(topCount, topReorder), this.cache.getSportList()]).pipe(
            map(([topSports, sports]) => {
                if (!topSports?.length) {
                    return undefined;
                }

                return { source: topSports, sports };
            }),
        );
    }

    private getSource(
        source: CountItem | CountItem[] | undefined,
        sports: CountItem[] | undefined,
        tournaments: Tournament[] | undefined,
        type: TopItemsType,
    ): SportTreeItem | undefined {
        if (this.isItemList(source) && type === TopItemsType.TopSports && sports) {
            return this.getAllLeftNavSports(source, sports);
        }

        if (this.isSingleItem(source)) {
            return type === TopItemsType.TopCountries
                ? this.competitionMapper.getTopRegions(source)
                : this.competitionMapper.getTopCompetitions(source, tournaments);
        }

        return;
    }

    private getAllLeftNavSports(source: CountItem[], sports: CountItem[]): SportTreeItem {
        const topSports = this.sportMapper.getTop(this.filterExceptModifyFirstEsport(source));

        const azSports = this.sortSports(this.sportMapper.get(this.filterExceptModifyFirstEsport(sports), true));
        azSports.forEach((sport) => delete sport.count);

        const leftNavSports: SportTreeItem = new LazyTreeItem([], { urlMapper: () => '', countMapper: () => 0 });
        leftNavSports.title = topSports.title;
        leftNavSports.children = [...topSports.children, new TreeSeparatorItem(topSports.subTitle ?? ''), ...azSports];

        return leftNavSports;
    }

    private filterExceptModifyFirstEsport(source: CountItem[]): CountItem[] {
        const firstIndex = source.findIndex((sport) => sport.isEsport);

        if (firstIndex !== -1) {
            const result = source.filter((sport) => !sport.isEsport);
            result.splice(firstIndex, 0, this.modifyEsportElement(source[firstIndex]));

            return result;
        }

        return source;
    }

    private modifyEsportElement(esport: CountItem): CountItem {
        const EsportsGroupElement = cloneDeep(esport);
        EsportsGroupElement.id = SportConstant.EsportsGroup;
        EsportsGroupElement.name = this.sitecore.eSports.ESports;
        EsportsGroupElement.icon = 'theme-e-sports';

        return EsportsGroupElement;
    }

    private isSingleItem(source: CountItem | CountItem[] | undefined): source is CountItem {
        return !Array.isArray(source);
    }

    private isItemList(source: CountItem | CountItem[] | undefined): source is CountItem[] {
        return Array.isArray(source);
    }

    private sortSports(items: SportTreeItem[]): SportTreeItem[] {
        return [...items].sort(this.byName);
    }

    private byName = (first: SportTreeItem, second: SportTreeItem): number => first.title.localeCompare(second.title);
}
